// src/components/TotalDisplay.tsx
import { useState, useEffect, useMemo } from 'react';
import { Group, Paper, Text, Stack, ActionIcon } from '@mantine/core';
import {
	IconArrowDownRight,
	IconArrowUpRight,
	IconReceipt2,
	IconAlertCircle,
} from '@tabler/icons-react';
import PastDueBillsModal from './PastDueBillsModal';
import { Bill } from '../types';

interface TotalDisplayProps {
	bills: Bill[];
	pastDueBills: Bill[];
	onMarkPastDuePaid: (billId: string) => void;
}

function TotalDisplay({
	bills,
	pastDueBills,
	onMarkPastDuePaid,
}: TotalDisplayProps) {
	const [showPastDueModal, setShowPastDueModal] = useState(false);
	const [showPercentages, setShowPercentages] = useState(false);

	useEffect(() => {
		const savedPreference = localStorage.getItem('show-bill-percentages');
		setShowPercentages(savedPreference === 'true');

		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === 'show-bill-percentages') {
				setShowPercentages(e.newValue === 'true');
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, []);

	// Memoize expensive calculations
	const { unpaidTotal, paidPercentage } = useMemo(() => {
		const unpaid = bills
			.filter((bill) => !bill.paid)
			.reduce((sum, bill) => sum + bill.amount, 0);

		const total = bills.reduce((sum, bill) => sum + bill.amount, 0);
		const paid = total - unpaid;
		const percentage = total > 0 ? Math.round((paid / total) * 100) : 0;

		return {
			unpaidTotal: unpaid,
			paidPercentage: percentage,
		};
	}, [bills]);

	const unpaidPastDueTotal = useMemo(
		() =>
			pastDueBills
				.filter((bill) => !bill.paid)
				.reduce((sum, bill) => sum + bill.amount, 0),
		[pastDueBills]
	);

	const hasPastDueBills = unpaidPastDueTotal > 0;

	return (
		<>
			<Stack gap='md'>
				{/* Top Cell - Total Unpaid */}
				<Paper withBorder p='md' radius='md'>
					<Group justify='space-between'>
						<Text size='xs' c='dimmed' tt='uppercase' fw={700}>
							Total Unpaid
						</Text>
						<Group gap='sm'>
							{hasPastDueBills && (
								<ActionIcon
									variant='subtle'
									color='red'
									onClick={() => setShowPastDueModal(true)}
									title='View Past Due Bills'
								>
									<IconAlertCircle size={22} stroke={1.5} />
								</ActionIcon>
							)}
							<IconReceipt2
								size={22}
								stroke={1.5}
								color='#228be6'
							/>
						</Group>
					</Group>

					<Group align='flex-end' gap='xs' mt={16}>
						<Text fz={30} fw={700} lh={1}>
							${unpaidTotal.toFixed(2)}
						</Text>
						{hasPastDueBills && (
							<Text fz='sm' c='red' fw={500} mb={1}>
								+${unpaidPastDueTotal.toFixed(2)} past due
							</Text>
						)}
					</Group>

					<Text fz='xs' c='dimmed' mt={4}>
						Total amount to be paid
					</Text>
				</Paper>

				{/* Bottom Row - Remaining and Paid side by side - Only shown if enabled */}
				{showPercentages && (
					<Group grow>
						{/* Remaining */}
						<Paper withBorder p='md' radius='md'>
							<Group justify='space-between'>
								<Text
									size='xs'
									c='dimmed'
									tt='uppercase'
									fw={700}
								>
									Remaining
								</Text>
								<IconArrowUpRight
									size={22}
									stroke={1.5}
									color='#FA5252'
								/>
							</Group>

							<Text fz={30} fw={700} mt={12} lh={1}>
								{100 - paidPercentage}%
							</Text>

							<Text fz='xs' c='dimmed' mt={4}>
								Of total bills
							</Text>
						</Paper>

						{/* Paid */}
						<Paper withBorder p='md' radius='md'>
							<Group justify='space-between'>
								<Text
									size='xs'
									c='dimmed'
									tt='uppercase'
									fw={700}
								>
									Paid
								</Text>
								<IconArrowDownRight
									size={22}
									stroke={1.5}
									color='#12B886'
								/>
							</Group>

							<Text fz={30} fw={700} mt={12} lh={1}>
								{paidPercentage}%
							</Text>

							<Text fz='xs' c='dimmed' mt={4}>
								Of total bills
							</Text>
						</Paper>
					</Group>
				)}
			</Stack>

			<PastDueBillsModal
				opened={showPastDueModal}
				onClose={() => setShowPastDueModal(false)}
				pastDueBills={pastDueBills}
				onMarkPaid={onMarkPastDuePaid}
			/>
		</>
	);
}

export default TotalDisplay;
